import classNames from 'classnames';
import { observer } from 'mobx-react';
import { cloneElement, MouseEvent, type JSX, useEffect, useState } from 'react';

import { OptionVariationState } from '../../../configurator/OptionState';
import css from './Popup.css';

interface PopupProps {
  onClick?: () => void;
  onHover?: () => void;
  onLeave?: () => void;
  className?: string;
  children: JSX.Element;
  popper?: JSX.Element;
  variation: OptionVariationState;
}

const Popup = observer(({ onClick, onHover, onLeave, className, children, popper, variation }: PopupProps) => {
  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Defer isVisible one tick behind isActive so the popper's CSS enter animation
  // runs (popperInit -> popperEntered).
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(isActive), 1);
    return () => clearTimeout(timer);
  }, [isActive]);

  // Auto-stop the highlight animation 500ms after it activates.
  const highlightActive = variation.highlightAnimation.active;
  useEffect(() => {
    if (!highlightActive) {
      return;
    }
    const timer = setTimeout(() => variation.highlightAnimation.stop(), 500);
    return () => clearTimeout(timer);
  }, [highlightActive, variation]);

  const handleClick = (e: MouseEvent<EventTarget>) => {
    e.preventDefault();
    onClick?.();
  };

  const handleMouseEnter = (e: MouseEvent<EventTarget>) => {
    e.preventDefault();
    onHover?.();
    setIsActive(true);
    setIsInitialized(true);
  };

  const handleMouseLeave = (e: MouseEvent<EventTarget>) => {
    e.preventDefault();
    onLeave?.();
    setIsActive(false);
  };

  const finalClassName = classNames(className, css.Popup, variation.highlightAnimation.active && css.scrolled);
  const popperClassName = classNames(css.popperInit, isActive && isVisible && css.popperEntered);

  return (
    <div className={finalClassName} id={variation.id}>
      {popper && isInitialized && cloneElement(popper, { className: popperClassName })}
      <div
        className={css.trigger}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </div>
  );
});

export default Popup;
